import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type AiPanelTab = 'write' | 'review' | 'comment'

interface UnifiedAiPanelState {
  tab: AiPanelTab
  setTab: (tab: AiPanelTab) => void

  // One-shot input seed for the Ask AI (`write`) tab. The Fix-with-AI flow
  // populates this when it hands a finding off to the Ask AI agent; the panel
  // reads and consumes it on mount / tab switch.
  //
  // `threadId` is OPTIONAL, and omitting it means "any thread". Fix-with-AI
  // switches threads as it hands off, so it pins the seed to the thread it is
  // moving the user to. The Pack's "Apply in section →" navigates in from
  // another page and has no thread to name — pinning it to a guess would make
  // the panel hold the seed forever.
  pendingInputSeed: string | null
  pendingThreadId: string | null
  setPendingSeed: (seed: string, threadId?: string) => void
  consumePendingSeed: () => { seed: string | null; threadId: string | null }
}

export const useUnifiedAiPanelStore = create<UnifiedAiPanelState>()(
  persist(
    (set, get) => ({
      tab: 'write',
      setTab: (tab) => set({ tab }),
      pendingInputSeed: null,
      pendingThreadId: null,
      setPendingSeed: (seed, threadId) =>
        set({ pendingInputSeed: seed, pendingThreadId: threadId ?? null }),
      consumePendingSeed: () => {
        const { pendingInputSeed, pendingThreadId } = get()
        if (pendingInputSeed === null) return { seed: null, threadId: null }
        set({ pendingInputSeed: null, pendingThreadId: null })
        return { seed: pendingInputSeed, threadId: pendingThreadId }
      },
    }),
    {
      name: 'bidcontext:unified-ai-panel',
      storage: createJSONStorage(() => localStorage),
      // Don't persist the one-shot seed.
      partialize: (state) => ({ tab: state.tab }),
    }
  )
)
